import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { groupAPI } from '../services/api';
import { useAuth } from './AuthContext';

const GroupContext = createContext();

export const useGroup = () => {
  const context = useContext(GroupContext);
  if (!context) {
    throw new Error('useGroup must be used within GroupProvider');
  }
  return context;
};

export const GroupProvider = ({ children }) => {
  const { groupIds, isSuperAdmin } = useAuth();
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load groups on mount
  useEffect(() => {
    loadGroups();
  }, [groupIds, isSuperAdmin]);

  // Set default selected group
  useEffect(() => {
    if (groups.length > 0 && !selectedGroupId) {
      // Try to get from localStorage first
      const savedGroupId = localStorage.getItem('selectedGroupId');
      if (savedGroupId && groups.find(g => g.id === parseInt(savedGroupId))) {
        setSelectedGroupId(parseInt(savedGroupId));
      } else {
        // Otherwise select first group
        setSelectedGroupId(groups[0].id);
      }
    }
  }, [groups, selectedGroupId]);

  const loadGroups = useCallback(async () => {
    try {
      setLoading(true);
      const response = await groupAPI.getAll();
      const groupsData = response.data.data || [];
      setGroups(groupsData);
    } catch (error) {
      console.error('Failed to load groups:', error);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  },[]);

  const selectGroup = (groupId) => {
    setSelectedGroupId(groupId);
    if (groupId) {
      localStorage.setItem('selectedGroupId', groupId);
    } else {
      localStorage.removeItem('selectedGroupId');
    }
  };

  const selectedGroup = groups.find(g => g.id === selectedGroupId);

  return (
    <GroupContext.Provider
      value={{
        groups,
        selectedGroup,
        selectedGroupId,
        selectGroup,
        loadGroups,
        loading,
      }}
    >
      {children}
    </GroupContext.Provider>
  );
};

